// MarketingLotes — /admin/marketing/lotes
// Lista de lotes de material impreso con KPIs de tracking.
// Cada lote tiene un slug único; el QR del volante apunta a limpack.uy/r/{slug}
// El Worker SSR registra cada visita en material_lote_visitas.

import { useEffect, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

const FORMATOS = ['A4', 'A5', 'A6', 'tarjeta', 'sticker', 'otro']

export default function MarketingLotes() {
  const [lotes, setLotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [edit, setEdit] = useState(null)
  const [msg, setMsg] = useState(null)

  async function cargar() {
    setLoading(true)
    const { data, error } = await supabase
      .from('material_lotes_kpi')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) setMsg({ tipo: 'error', txt: error.message })
    setLotes(data || [])
    setLoading(false)
  }

  useEffect(() => { cargar() }, [])

  async function guardar(form) {
    const payload = {
      slug: form.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
      formato: form.formato,
      descripcion: form.descripcion || null,
      cantidad: Number(form.cantidad) || 0,
      destino: form.destino || null,
      redirect_to: form.redirect_to || 'https://limpack.uy',
      activo: form.activo !== false,
      notas: form.notas || null,
    }
    let resp
    if (edit?.id) {
      resp = await supabase.from('material_lotes').update(payload).eq('id', edit.id)
    } else {
      resp = await supabase.from('material_lotes').insert(payload)
    }
    if (resp.error) {
      setMsg({ tipo: 'error', txt: resp.error.message })
      return
    }
    setMsg({ tipo: 'ok', txt: edit ? 'Lote actualizado' : 'Lote creado' })
    setShowForm(false)
    setEdit(null)
    cargar()
  }

  async function eliminar(id, slug) {
    if (!confirm(`¿Eliminar lote "${slug}"? Las visitas registradas se borran también.`)) return
    const { error } = await supabase.from('material_lotes').delete().eq('id', id)
    if (error) { setMsg({ tipo: 'error', txt: error.message }); return }
    setMsg({ tipo: 'ok', txt: 'Lote eliminado' })
    cargar()
  }

  return (
    <AdminLayout title="Lotes de material impreso">
      <div className="ml-wrap">
        <header className="ml-head">
          <div>
            <div className="ml-eyebrow">MARKETING · TRACKING QR</div>
            <h1 className="ml-title">Lotes de material impreso</h1>
            <div className="ml-sub">Cada lote tiene un QR único en <code>limpack.uy/r/[slug]</code>. Vemos quién entra desde cada pieza.</div>
          </div>
          <div className="ml-head-acts">
            <Link to="/admin/marketing/volante" className="ml-btn-ghost">📄 Builder volantes</Link>
            <Link to="/admin/marketing" className="ml-btn-ghost">← Hub</Link>
            <button onClick={() => { setEdit(null); setShowForm(true) }} className="ml-btn-primary">+ Nuevo lote</button>
          </div>
        </header>

        {msg && (
          <div className={`ml-msg ${msg.tipo}`} onClick={() => setMsg(null)}>
            {msg.txt} <span style={{ float: 'right', cursor: 'pointer' }}>×</span>
          </div>
        )}

        {showForm && (
          <FormLote
            inicial={edit}
            onCancel={() => { setShowForm(false); setEdit(null) }}
            onSubmit={guardar}
          />
        )}

        {loading ? (
          <div className="ml-empty">Cargando lotes…</div>
        ) : lotes.length === 0 ? (
          <div className="ml-empty">
            <div className="ml-empty-icon">📦</div>
            <div className="ml-empty-title">No hay lotes todavía</div>
            <div className="ml-empty-sub">Creá uno arriba con un slug único y un destino. Después imprimís el QR apuntando a <code>limpack.uy/r/[slug]</code>.</div>
          </div>
        ) : (
          <div className="ml-cards">
            {lotes.map(l => (
              <LoteCard
                key={l.id}
                lote={l}
                onEdit={() => { setEdit(l); setShowForm(true) }}
                onDelete={() => eliminar(l.id, l.slug)}
              />
            ))}
          </div>
        )}

        <div className="ml-hint-box">
          <strong>Workflow:</strong>
          <ol>
            <li>Crear lote → te genera un slug único, ej <code>a4-pocitos-001</code></li>
            <li>Diseñá el volante en <Link to="/admin/marketing/volante">/admin/marketing/volante</Link> usando ese slug</li>
            <li>Imprimí — el QR ya apunta a <code>limpack.uy/r/[slug]</code></li>
            <li>Repartí y volvé acá a ver visitas por lote / barrio / horario</li>
          </ol>
        </div>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

function LoteCard({ lote, onEdit, onDelete }) {
  const tasa = lote.tasa_conversion_pct
  return (
    <div className={`ml-card ${!lote.activo ? 'inactivo' : ''}`}>
      <div className="ml-card-top">
        <div>
          <span className="ml-fmt-badge">{lote.formato}</span>
          {!lote.activo && <span className="ml-inactivo-badge">pausado</span>}
          <div className="ml-slug">limpack.uy/r/<strong>{lote.slug}</strong></div>
          <div className="ml-desc">{lote.descripcion || <em>sin descripción</em>}</div>
          {lote.destino && <div className="ml-destino">📍 {lote.destino}</div>}
        </div>
        <div className="ml-card-acts">
          <button onClick={onEdit} title="Editar">✎</button>
          <button onClick={onDelete} title="Eliminar" className="ml-rm">×</button>
        </div>
      </div>

      <div className="ml-kpis">
        <div className="ml-kpi">
          <div className="ml-kpi-num">{lote.visitas_total || 0}</div>
          <div className="ml-kpi-lbl">visitas total</div>
        </div>
        <div className="ml-kpi">
          <div className="ml-kpi-num">{lote.visitas_7d || 0}</div>
          <div className="ml-kpi-lbl">últimos 7 días</div>
        </div>
        <div className="ml-kpi">
          <div className="ml-kpi-num">{lote.visitantes_unicos || 0}</div>
          <div className="ml-kpi-lbl">únicos</div>
        </div>
        <div className="ml-kpi">
          <div className="ml-kpi-num">{lote.cantidad || 0}</div>
          <div className="ml-kpi-lbl">impresos</div>
        </div>
        {tasa !== null && tasa !== undefined && (
          <div className="ml-kpi destacado">
            <div className="ml-kpi-num">{tasa}%</div>
            <div className="ml-kpi-lbl">tasa conversión</div>
          </div>
        )}
      </div>

      <div className="ml-meta">
        Creado {fmtFecha(lote.created_at)}
        {lote.ultima_visita && <> · última visita {fmtFecha(lote.ultima_visita)}</>}
      </div>
    </div>
  )
}

function FormLote({ inicial, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    slug: inicial?.slug || '',
    formato: inicial?.formato || 'A4',
    descripcion: inicial?.descripcion || '',
    cantidad: inicial?.cantidad || 0,
    destino: inicial?.destino || '',
    redirect_to: inicial?.redirect_to || 'https://limpack.uy',
    activo: inicial?.activo !== false,
    notas: inicial?.notas || '',
  })

  function set(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  return (
    <form className="ml-form" onSubmit={e => { e.preventDefault(); onSubmit(form) }}>
      <div className="ml-form-grid">
        <div>
          <label className="ml-lbl">Slug (URL del QR)</label>
          <input
            type="text"
            value={form.slug}
            onChange={e => set('slug', e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase())}
            placeholder="a4-pocitos-001"
            className="ml-input"
            required
            disabled={!!inicial?.id}
          />
          <div className="ml-help">limpack.uy/r/{form.slug || '…'}</div>
        </div>
        <div>
          <label className="ml-lbl">Formato</label>
          <select value={form.formato} onChange={e => set('formato', e.target.value)} className="ml-input">
            {FORMATOS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="ml-full">
          <label className="ml-lbl">Descripción</label>
          <input type="text" value={form.descripcion} onChange={e => set('descripcion', e.target.value)}
                 placeholder="Hall edificio Pocitos, junio" className="ml-input" />
        </div>
        <div>
          <label className="ml-lbl">Cantidad impresa</label>
          <input type="number" min="0" value={form.cantidad} onChange={e => set('cantidad', e.target.value)} className="ml-input" />
        </div>
        <div>
          <label className="ml-lbl">Destino / barrio</label>
          <input type="text" value={form.destino} onChange={e => set('destino', e.target.value)}
                 placeholder="Pocitos" className="ml-input" />
        </div>
        <div className="ml-full">
          <label className="ml-lbl">Redirigir a (URL destino del QR)</label>
          <input type="url" value={form.redirect_to} onChange={e => set('redirect_to', e.target.value)} className="ml-input" />
          <div className="ml-help">Default: limpack.uy. Podés mandar a /vecinos o a una landing específica.</div>
        </div>
        <div className="ml-full">
          <label className="ml-lbl">Notas internas</label>
          <textarea value={form.notas} onChange={e => set('notas', e.target.value)} rows="2" className="ml-input"
                    placeholder="Quién lo repartió, cuándo, etc"></textarea>
        </div>
        <div>
          <label className="ml-lbl">Estado</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
            <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} />
            <span style={{ fontSize: 13 }}>{form.activo ? 'Activo (acepta visitas)' : 'Pausado'}</span>
          </label>
        </div>
      </div>
      <div className="ml-form-acts">
        <button type="button" onClick={onCancel} className="ml-btn-ghost">Cancelar</button>
        <button type="submit" className="ml-btn-primary">{inicial ? 'Guardar cambios' : 'Crear lote'}</button>
      </div>
    </form>
  )
}

function fmtFecha(s) {
  if (!s) return ''
  const d = new Date(s)
  const ahora = new Date()
  const diff = (ahora - d) / 1000
  if (diff < 60) return 'hace segundos'
  if (diff < 3600) return `hace ${Math.floor(diff/60)} min`
  if (diff < 86400) return `hace ${Math.floor(diff/3600)} h`
  if (diff < 86400*7) return `hace ${Math.floor(diff/86400)} d`
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: '2-digit' })
}

function Estilos() {
  return (
    <style>{`
      .ml-wrap { max-width: 1300px; margin: 0 auto; padding: 0 4px 60px; }
      .ml-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .ml-eyebrow { font-size: 10.5px; letter-spacing: .8px; color: #6B7280; }
      .ml-title { margin: 2px 0 0; font-size: 18px; font-weight: 600; color: #1A3461; }
      .ml-sub { font-size: 12px; color: #6B7280; margin-top: 4px; }
      .ml-sub code { background: #F8F6F1; padding: 1px 5px; border-radius: 3px; color: #00A8B5; }
      .ml-head-acts { display: flex; gap: 8px; flex-wrap: wrap; }

      .ml-btn-primary { padding: 8px 14px; border-radius: 8px; font-size: 13px; background: #00A8B5; color: #fff; border: 0; cursor: pointer; font-family: inherit; font-weight: 600; }
      .ml-btn-primary:hover { background: #008792; }
      .ml-btn-ghost { padding: 8px 14px; border-radius: 8px; font-size: 13px; background: #fff; color: #1A3461; border: 1px solid #E6E2D8; text-decoration: none; cursor: pointer; font-family: inherit; }
      .ml-btn-ghost:hover { background: #F8F6F1; }

      .ml-msg { padding: 10px 14px; border-radius: 8px; margin-bottom: 12px; font-size: 13px; cursor: pointer; }
      .ml-msg.ok { background: #D1FAE5; border: 1px solid #10B981; color: #065F46; }
      .ml-msg.error { background: #FEE2E2; border: 1px solid #DC2626; color: #991B1B; }

      .ml-form { background: #fff; border: 1px solid #C5A059; border-radius: 10px; padding: 18px; margin-bottom: 12px; }
      .ml-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .ml-full { grid-column: 1 / -1; }
      .ml-lbl { display: block; font-size: 11px; color: #6B7280; margin-bottom: 4px; font-weight: 600; letter-spacing: .5px; text-transform: uppercase; }
      .ml-input { width: 100%; padding: 8px 10px; border: 1px solid #E6E2D8; border-radius: 6px; font-size: 13px; font-family: inherit; background: #fff; color: #1A3461; box-sizing: border-box; }
      .ml-input:disabled { background: #F8F6F1; cursor: not-allowed; }
      .ml-help { font-size: 10.5px; color: #6B7280; margin-top: 3px; }
      .ml-form-acts { display: flex; gap: 8px; justify-content: flex-end; margin-top: 14px; }

      .ml-empty { background: #fff; border: 1px dashed #E6E2D8; border-radius: 10px; padding: 50px 20px; text-align: center; }
      .ml-empty-icon { font-size: 36px; margin-bottom: 10px; }
      .ml-empty-title { font-size: 16px; font-weight: 600; color: #1A3461; margin-bottom: 4px; }
      .ml-empty-sub { font-size: 13px; color: #6B7280; max-width: 460px; margin: 0 auto; line-height: 1.5; }
      .ml-empty-sub code { background: #F8F6F1; padding: 1px 5px; border-radius: 3px; color: #00A8B5; }

      .ml-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 12px; }
      .ml-card { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px; }
      .ml-card.inactivo { opacity: 0.6; }
      .ml-card-top { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; }
      .ml-fmt-badge { display: inline-block; background: #1A3461; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 10.5px; font-weight: 700; letter-spacing: .5px; margin-bottom: 6px; margin-right: 4px; }
      .ml-inactivo-badge { display: inline-block; background: #94A3B8; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 10.5px; font-weight: 700; }
      .ml-slug { font-size: 12px; color: #1A3461; margin-bottom: 4px; font-family: SF Mono, Monaco, monospace; }
      .ml-slug strong { color: #00A8B5; }
      .ml-desc { font-size: 13.5px; color: #1A3461; font-weight: 600; }
      .ml-desc em { color: #94A3B8; font-style: normal; font-weight: 400; }
      .ml-destino { font-size: 11.5px; color: #6B7280; margin-top: 2px; }
      .ml-card-acts { display: flex; gap: 4px; }
      .ml-card-acts button { background: #F8F6F1; border: 1px solid #E6E2D8; border-radius: 4px; font-size: 11px; padding: 3px 7px; cursor: pointer; color: #1A3461; }
      .ml-card-acts button:hover { background: #E0F4F6; }
      .ml-rm { color: #DC2626 !important; font-weight: 700; }

      .ml-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(70px, 1fr)); gap: 8px; margin: 12px 0; padding: 10px; background: #F8F6F1; border-radius: 6px; }
      .ml-kpi { text-align: center; }
      .ml-kpi.destacado .ml-kpi-num { color: #00A8B5; }
      .ml-kpi-num { font-size: 18px; font-weight: 700; color: #1A3461; line-height: 1; }
      .ml-kpi-lbl { font-size: 9.5px; color: #6B7280; margin-top: 3px; letter-spacing: .3px; }

      .ml-meta { font-size: 11px; color: #94A3B8; margin-top: 8px; padding-top: 8px; border-top: 1px solid #F1EFE8; }

      .ml-hint-box { background: #FFFBEF; border: 1px solid #E6D9B8; border-radius: 10px; padding: 14px 18px; margin-top: 18px; font-size: 12.5px; color: #1A3461; }
      .ml-hint-box ol { margin: 8px 0 0; padding-left: 22px; line-height: 1.65; }
      .ml-hint-box code { background: #fff; padding: 1px 5px; border-radius: 3px; color: #00A8B5; font-size: 11.5px; }
    `}</style>
  )
}
